import React, { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX, X, Play, Pause, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { iceLogo } from '../mock';

const IAN_PHOTO = 'https://customer-assets-gfyr7b9c.emergentagent.net/job_navy-red-hub/artifacts/wctlpnn3_ed9addf9-8470-44d5-9e7e-aca224fb1a62.jpeg';

const GREETING_TEXT = `Welcome to ICE Brookline Jamaica Plain Pop-Warner Football & Cheerleading, renamed in honor of Ian Cotterell. Ian was one of the founders of the Brookline Jamaica Plain Patriots. He was tremendously loved and respected by everyone who came through the program because of his passion, dedication, and commitment to helping the young men and women in the program develop into respectful and responsible adults. Although Ian has passed on, we strive to continue his mission of building a strong community and uplifting children through the sport he loved most. Welcome to our page! We hope to see you all on the field, cheering on our young athletes and supporting them as they grow both on and off the field. Go Patriots!`;

const SESSION_KEY = 'bjp-welcome-greeter-shown';

export default function WelcomeGreeter() {
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [usingAudio, setUsingAudio] = useState(false); // true when MP3 plays, false = browser TTS
  const audioRef = useRef(null);
  const startedRef = useRef(false);

  const supportsTTS = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const AUDIO_URL = `${BACKEND}/api/tts/ian-tribute.mp3`;

  // Show on first visit per session
  useEffect(() => {
    try {
      const shown = sessionStorage.getItem(SESSION_KEY);
      if (!shown) setOpen(true);
    } catch (_e) {
      setOpen(true);
    }
  }, []);

  // On modal open, attempt muted autoplay (browsers allow muted media autoplay).
  // First hover/click on the modal will unmute — Netflix trailer style.
  useEffect(() => {
    if (!open || startedRef.current) return;
    startedRef.current = true;

    const speakViaBrowser = () => {
      if (!supportsTTS) return;
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(GREETING_TEXT);
      utter.rate = 0.98;
      utter.pitch = 1.0;
      utter.volume = 1.0;
      const voices = window.speechSynthesis.getVoices();
      const preferred = voices.find((v) => /en-US|English/i.test(`${v.lang} ${v.name}`) && /male|Daniel|Alex|Fred|Google US English/i.test(v.name))
        || voices.find((v) => /^en(-|_)?US/i.test(v.lang))
        || voices.find((v) => /^en/i.test(v.lang));
      if (preferred) utter.voice = preferred;
      utter.onstart = () => setSpeaking(true);
      utter.onend = () => setSpeaking(false);
      utter.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(utter);
    };

    const attemptAutoplay = async () => {
      try {
        const audio = new Audio(AUDIO_URL);
        audio.preload = 'auto';
        audio.muted = true; // browsers allow muted autoplay
        audioRef.current = audio;
        audio.onplay = () => { setSpeaking(true); setUsingAudio(true); };
        audio.onended = () => { setSpeaking(false); };
        audio.onerror = () => { setSpeaking(false); setUsingAudio(false); };
        await audio.play();
      } catch (_e) {
        // Even muted autoplay blocked — stay silent, user can tap Play Tribute
        setUsingAudio(false);
      }
    };

    attemptAutoplay();
  }, [open, supportsTTS, AUDIO_URL]);

  // Any hover / click on the modal body unmutes the running audio ("Netflix trailer" moment)
  const handleUserGesture = () => {
    if (audioRef.current && audioRef.current.muted && !muted) {
      audioRef.current.muted = false;
    }
  };

  const startNarration = () => {
    if (muted || speaking) return;
    if (audioRef.current) {
      audioRef.current.muted = false;
      audioRef.current.play().catch(() => {});
      return;
    }
    // Fallback: create audio + play (user gesture — always allowed)
    const audio = new Audio(AUDIO_URL);
    audio.preload = 'auto';
    audioRef.current = audio;
    audio.onplay = () => { setSpeaking(true); setUsingAudio(true); };
    audio.onended = () => { setSpeaking(false); };
    audio.onerror = () => {
      setSpeaking(false); setUsingAudio(false);
      if (supportsTTS) {
        window.speechSynthesis.cancel();
        const utter = new SpeechSynthesisUtterance(GREETING_TEXT);
        utter.onstart = () => setSpeaking(true);
        utter.onend = () => setSpeaking(false);
        window.speechSynthesis.speak(utter);
      }
    };
    audio.play().catch(() => {});
  };

  // Cleanup on close
  useEffect(() => {
    if (!open) return;
    return () => {
      if (audioRef.current) { try { audioRef.current.pause(); } catch (_e) {} }
      if (supportsTTS) window.speechSynthesis.cancel();
    };
  }, [open, supportsTTS]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  const dismiss = () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch (_e) {} }
    if (supportsTTS) window.speechSynthesis.cancel();
    setSpeaking(false);
    setOpen(false);
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (_e) {}
  };

  const toggleMute = () => {
    if (muted) {
      setMuted(false);
    } else {
      if (audioRef.current) { try { audioRef.current.pause(); } catch (_e) {} }
      if (supportsTTS) window.speechSynthesis.cancel();
      setSpeaking(false);
      setMuted(true);
    }
  };

  const replay = () => {
    if (muted) return;
    if (audioRef.current) { try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch (_e) {} }
    if (supportsTTS) window.speechSynthesis.cancel();
    setSpeaking(false);
    setTimeout(() => startNarration(), 30);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-[#0a1f44]/95 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-greeter-title"
      data-testid="welcome-greeter"
    >
      <div
        className="relative w-full max-w-3xl bg-[#0a1f44] border-t-4 border-[#c8102e] shadow-2xl max-h-[94dvh] sm:max-h-[90vh] overflow-hidden flex flex-col"
        onMouseEnter={handleUserGesture}
        onClick={handleUserGesture}
      >
        {/* Skip / close */}
        <button
          type="button"
          onClick={dismiss}
          data-testid="welcome-greeter-skip"
          className="absolute top-2 right-2 z-10 min-w-11 min-h-11 inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-heading uppercase text-[10px] px-3 py-2 transition-colors"
          aria-label="Skip greeting"
        >
          Skip <X className="w-3.5 h-3.5" />
        </button>

        {/* Header with Ian's portrait */}
        <div className="px-4 sm:px-8 pt-12 sm:pt-10 pb-3 sm:pb-4 text-white text-center">
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-3 sm:mb-4">
            <img
              src={IAN_PHOTO}
              alt="Ian Cotterell — founder"
              data-testid="welcome-greeter-ian-photo"
              className="w-16 h-16 sm:w-24 sm:h-24 md:w-28 md:h-28 object-cover border-2 border-[#c8102e] shadow-lg"
              style={{ objectPosition: '72% 24%', transform: 'scale(1.55)', transformOrigin: '72% 24%' }}
            />
            <img src={iceLogo} alt="ICE Brookline JP Patriots" className="w-12 sm:w-16 md:w-20 h-auto" />
          </div>
          <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px] mb-2">In Memory of · Ian Cotterell · 1969 — Forever</div>
          <h2 id="welcome-greeter-title" className="font-display text-2xl sm:text-3xl md:text-4xl leading-tight">
            WELCOME TO <span className="text-[#c8102e]">ICE</span> BROOKLINE JP PATRIOTS
          </h2>
        </div>

        {/* Message + live indicator */}
        <div className="px-4 sm:px-8 pb-4 flex-1 overflow-y-auto overscroll-contain" data-testid="welcome-greeter-text">
          <div className="flex items-center gap-2 justify-center mb-4">
            <span className={`w-2 h-2 rounded-full ${speaking ? 'bg-[#c8102e] animate-pulse' : 'bg-white/30'}`} />
            <span className="font-heading uppercase tracking-widest text-[10px] text-white/60">
              {muted
                ? 'Voice muted'
                : speaking
                  ? (audioRef.current && audioRef.current.muted
                      ? 'Playing silently · hover or tap anywhere to unmute'
                      : (usingAudio ? 'Narrated by Onyx · playing…' : 'Now reading aloud…'))
                  : (supportsTTS ? 'Tap ▶ to hear Ian\'s story' : 'Text-to-speech unavailable — reading below')}
            </span>
          </div>

          {/* Play/Unmute button — always available so users always have a tap target */}
          {!muted && (
            <div className="flex justify-center mb-6">
              <button
                type="button"
                onClick={startNarration}
                data-testid="welcome-greeter-play"
                className="min-h-12 inline-flex items-center gap-3 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase text-sm px-6 sm:px-8 py-3 sm:py-4 shadow-lg shadow-[#c8102e]/40 hover:shadow-[#c8102e]/70 transition-[background-color,box-shadow] animate-pulse"
              >
                {speaking && audioRef.current && audioRef.current.muted
                  ? (<><Volume2 className="w-5 h-5" /> Tap to Unmute</>)
                  : (<><Play className="w-5 h-5" /> Play Tribute</>)}
              </button>
            </div>
          )}
          <p className="text-white/90 leading-relaxed text-sm sm:text-base md:text-lg text-left sm:text-center">
            {GREETING_TEXT}
          </p>
        </div>

        {/* Footer controls */}
        <div className="border-t border-white/10 px-4 sm:px-6 py-3 sm:py-4 grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center gap-2 sm:gap-3 bg-black/20 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:pb-4">
          {(supportsTTS || true) && (
            <>
              <button
                type="button"
                onClick={toggleMute}
                data-testid="welcome-greeter-mute"
                className={`inline-flex items-center justify-center gap-2 font-heading uppercase text-[10px] px-3 sm:px-4 py-2.5 border transition-colors ${muted ? 'bg-white text-[#0a1f44] border-white' : 'border-white/30 text-white hover:border-[#c8102e]'}`}
                aria-pressed={muted}
              >
                {muted ? <><Volume2 className="w-4 h-4" /> Unmute</> : <><VolumeX className="w-4 h-4" /> Mute</>}
              </button>
              <button
                type="button"
                onClick={replay}
                disabled={muted}
                data-testid="welcome-greeter-replay"
                className="inline-flex items-center justify-center gap-2 font-heading uppercase text-[10px] px-3 sm:px-4 py-2.5 border border-white/30 text-white hover:border-[#c8102e] transition-colors disabled:opacity-40"
              >
                {speaking ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {speaking ? 'Speaking…' : 'Replay'}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={dismiss}
            data-testid="welcome-greeter-enter"
            className="col-span-2 sm:col-span-1 sm:order-none order-first w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase text-[10px] px-5 sm:px-6 py-2.5 transition-colors"
          >
            Go Patriots — Enter Site
          </button>
          <Link
            to="/tribute"
            onClick={dismiss}
            data-testid="welcome-greeter-tribute-link"
            className="col-span-2 sm:col-span-1 inline-flex items-center justify-center gap-2 border border-white/30 text-white hover:border-[#c8102e] font-heading uppercase text-[10px] px-4 py-2.5 transition-colors"
          >
            Read Ian's Full Tribute <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
